/**
 * Biometric Slider Component for Alpine.js
 * Provides a tactile, ruler-style interaction for metric inputs.
 */

export function BiometricSlider(config = {}) {
    return {
        value: config.value || 0,
        min: config.min || 0,
        max: config.max || 250,
        step: config.step || 0.1,
        metricName: config.metricName || null,
        isDragging: false,
        startX: 0,
        startValue: 0,
        pixelsPerUnit: 100, // 1 unit = 100px

        init() {
            // Ensure initial value is within bounds and rounded
            this.value = this.round(this.value);

            this.$watch('value', (v) => {
                const rounded = this.round(v);
                if (rounded !== this.value) {
                    this.value = rounded;
                }
                if (this.metricName) {
                    this.$dispatch('metric-changed', {
                        name: this.metricName,
                        value: this.value,
                    });
                }
            });

            // L/R sync: mirror a counterpart slider's value
            window.addEventListener('lr-sync', (e) => {
                if (e.detail.name === this.metricName) {
                    this.value = this.round(e.detail.value);
                }
            });
        },

        round(val) {
            const snapped = Math.max(this.min, Math.min(this.max, val));
            const factor = 1 / (this.step || 0.1);
            return Math.round(snapped * factor) / factor;
        },

        get offset() {
            return (this.value - this.min) * this.pixelsPerUnit;
        },

        get rulerWidth() {
            return (this.max - this.min) * this.pixelsPerUnit;
        },

        startDrag(e) {
            this.isDragging = true;
            this.startX = this.getClientX(e);
            this.startValue = this.value;

            // Prevent text selection during drag
            document.body.classList.add('select-none', 'cursor-grabbing');

            // Use pointer capture if available
            if (e.target.setPointerCapture) {
                e.target.setPointerCapture(e.pointerId);
            }
        },

        doDrag(e) {
            if (!this.isDragging) return;

            const currentX = this.getClientX(e);
            const deltaX = currentX - this.startX;
            const deltaValue = deltaX / this.pixelsPerUnit;

            const newValue = this.round(this.startValue - deltaValue);
            if (newValue !== this.value) {
                this.value = newValue;
                // Subtle haptic feedback on change
                if (window.navigator.vibrate) {
                    window.navigator.vibrate(5);
                }
            }
        },

        endDrag(e) {
            if (!this.isDragging) return;
            this.isDragging = false;
            document.body.classList.remove('select-none', 'cursor-grabbing');

            if (e && e.target.releasePointerCapture) {
                e.target.releasePointerCapture(e.pointerId);
            }
        },

        onWheel(e) {
            // Fine tune with scroll wheel
            const direction = e.deltaY > 0 ? 1 : -1;
            this.value = this.round(this.value + direction * this.step);
        },

        getClientX(e) {
            return e.clientX ?? e.touches?.[0]?.clientX ?? (e.pointerId && e.clientX) ?? 0;
        },

        updateFromInput(val) {
            const normalized = String(val).replace(',', '.');
            const parsed = parseFloat(normalized);
            if (!isNaN(parsed)) {
                this.value = this.round(parsed);
            }
        },
    };
}
